import { Mui } from "@osu/react-ui";
import { useState } from 'react';
import { useGetStudentRequestReasonsQuery, useChangeRequestedMutation } from '../../services/graphql/api';
import { STUDENT_REQUEST_ACTIONS } from "../../util/constants";

const ChangeRequestedDialog = ({ openDialog, setOpenDialog, applicant, isAdmin }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [action, setAction] = useState("");
    const [reason, setReason] = useState("");
    const [caseNumber, setCaseNumber] = useState("");
    const { data: studentRequestReasons } = useGetStudentRequestReasonsQuery();
    const [changeRequested, result] = useChangeRequestedMutation();
    const isLoadingOrFinished = !!result?.isLoading || !!showSuccess || !!errorMessage;
    const caseNumberIsValid = isAdmin
      ? (caseNumber.length === 0 || /^\d{6,10}$/.test(caseNumber))
      : /^\d{6,10}$/.test(caseNumber);
    const isFormValid = action && reason && caseNumberIsValid;
   
    if (!applicant) { return null; }

    const dialogMessage = `Fill out form to request a change. Applicant ${applicant?.firstName} ${applicant?.lastName} (ID: ${applicant?.applicantEmployeeId}) is currently in the ${applicant?.programId} program.`;

    async function confirmAction(e) {
        e.preventDefault();
        e.stopPropagation();
        if (isSubmitting) return;
        setIsSubmitting(true);

        try {
            const input = {
                applicantEmployeeId: applicant.applicantEmployeeId,
                programId: applicant.programId,
                term: applicant.term,
                requestType: action,
                reason,
                caseNumber: caseNumber ? parseInt(caseNumber) : undefined,
            };
            const result = await changeRequested({ input });
            
            if (result.data?.status === "success") {
                setShowSuccess(true);
                setErrorMessage("");
            } else {
                setErrorMessage(result.data?.errorDetails?.[0] || "An error occurred while processing the request.");
                setShowSuccess(false);
            }
        } catch (error) {
            console.error("Error occurred while requesting change:", error);
        } finally {
            setIsSubmitting(false);
        }
    }

    const handleClose = () => {
        setOpenDialog(false);
        setTimeout(() => { // Prevent dialog from transforming weirdly while closing
            setShowSuccess(false);
            setErrorMessage("");
            setAction("");
            setReason("");
            setCaseNumber("");
        }, 500);
    };

    return (
        <Mui.Dialog open={openDialog} fullWidth maxWidth="sm">
            <Mui.DialogTitle>Flag Applicant</Mui.DialogTitle>
            {showSuccess && (
                <Mui.Alert severity="success" sx={{ margin: 2, marginBottom: 0 }}>
                    Request submitted successfully!
                </Mui.Alert>
            )}
            {errorMessage && (
                <Mui.Alert severity="error" sx={{ margin: 2, marginBottom: 0 }}>
                    {errorMessage}
                </Mui.Alert>       
            )}
            <Mui.DialogContent>
                {!showSuccess && (
                    <>
                        <Mui.DialogContentText>{dialogMessage}</Mui.DialogContentText>
                        <Mui.FormControl fullWidth sx={{ mt: 2 }}>
                            <Mui.InputLabel id="action-label">Action</Mui.InputLabel>
                            <Mui.Select
                                disabled={isLoadingOrFinished}
                                labelId="action-label"
                                value={action}
                                label="Action"
                                onChange={e => setAction(e.target.value)}
                                required
                            >
                                {Object.values(STUDENT_REQUEST_ACTIONS).map(opt => (
                                    <Mui.MenuItem key={opt.KEY} value={opt.KEY}>{opt.label}</Mui.MenuItem>
                                ))}
                            </Mui.Select>
                        </Mui.FormControl>
                        <Mui.FormControl fullWidth sx={{ mt: 2 }}>
                            <Mui.InputLabel id="reason-label">Reason</Mui.InputLabel>
                            <Mui.Select
                                disabled={isLoadingOrFinished}
                                labelId="reason-label"
                                value={reason}
                                label="Reason"
                                onChange={e => setReason(e.target.value)}
                                required
                            >
                                {studentRequestReasons?.map?.(opt => (
                                    <Mui.MenuItem key={opt.KEY} value={opt.KEY}>{opt.LABEL}</Mui.MenuItem>
                                ))}
                            </Mui.Select>
                        </Mui.FormControl>
                        <Mui.TextField
                            disabled={isLoadingOrFinished}
                            label="Case Number"
                            fullWidth
                            sx={{ mt: 2 }}
                            value={caseNumber}
                            onChange={e => setCaseNumber(e.target.value.replace(/\D/g, ""))}
                            required={!isAdmin}
                            error={caseNumber.length > 0 && !caseNumberIsValid}
                            helperText={caseNumber.length > 0 && !caseNumberIsValid ? "Case number must be 6-10 digits." : ""}
                        />
                    </>
                )}
            </Mui.DialogContent>
            <Mui.DialogActions className="padding-2">
                {showSuccess || errorMessage ? (
                    <Mui.Button color="primary" variant="contained" onClick={handleClose}>
                        Close
                    </Mui.Button>
                ) : (
                    <>
                        <Mui.Button color="secondary" variant="outlined" onClick={() => handleClose(true)}>Cancel</Mui.Button>
                        <Mui.Button
                            disabled={isSubmitting || !isFormValid}
                            color="primary"
                            variant="contained"
                            onClick={confirmAction}
                        >
                            {isSubmitting && <Mui.CircularProgress size={20} style={{ marginRight: '.5rem' }} />}
                            Continue
                        </Mui.Button>
                    </>
                )}
            </Mui.DialogActions>
        </Mui.Dialog>
    );
};

export default ChangeRequestedDialog;

